import { isAxiosError } from 'axios';
import { FirebaseError } from 'firebase/app';
import type {
  FieldErrors, FieldValues, DeepRequired, FieldErrorsImpl,
} from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

export const getApiError = (e: any) => {
  const status = e.status || (e.response ? e.response.status : 0);

  if (status === 401) {
    return {
      message: 'error.sessionExpired',
      title: 'Your session has expired!',
      status: 401,
    };
  }

  if (isAxiosError(e) && e?.response?.data) {
    return {
      ...e?.response?.data,
      status,
    };
  }

  if (e?.detail) {
    return {
      ...e,
      status,
    };
  }

  return {
    message: e?.response?.statusText || 'Something went wrong!',
    title: e?.message || 'Something went wrong!',
    status,
  };
};

interface ErrorHandlers {
  onFirebaseError?: (error: FirebaseError) => void;
  onUnknownError?: (error: unknown) => void;
}

export const handleApiErrors = (error: unknown, handlers: ErrorHandlers = {}) => {
  if (error instanceof FirebaseError) {
    handlers.onFirebaseError?.(error);
    return;
  }

  handlers.onUnknownError?.(error);
};

export const getTranslatedErrors = <T extends FieldValues>(
  errors: Partial<FieldErrors<T>>,
): Partial<FieldErrors<T>> => {
  let formattedErrors = {} as Partial<FieldErrors<T>>;
  type ErrorKey = keyof T;

  Object.keys(errors).forEach((value) => {
    const key = value as ErrorKey;
    const errorAsArray = errors[key] as FieldErrorsImpl<DeepRequired<string[]>>;

    if (errorAsArray?.length) {
      errorAsArray.forEach((e, index: number) => {
        if (e?.message) {
          const message = e?.message;

          formattedErrors = {
            ...formattedErrors,
            [`${key.toString()}-${index}`]: {
              ...[e],
              message: <FormattedMessage id={message} />,
            },
          };
        }
      });
    }

    if (errors[key]?.message) {
      const message = errors[key]?.message as string;

      formattedErrors = {
        ...formattedErrors,
        [key]: {
          ...errors[key],
          message: <FormattedMessage id={message} />,
        },
      };
    }
  });

  return formattedErrors;
};
